import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { asyncForEach, showError } from '@utils';
import { useQuery } from 'react-query';
import { orderBy } from 'lodash-es';

import {
  boardsServices,
  fleetVehiclesServices,
  servicePointsServices,
} from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { ARNM_ACCIDENTS_TASK_STATUSES_NAMES } from 'components/arnmAccidents/constants';

import { useAssignees, useServicePoints } from 'hooks/common';
import {
  handleArnmAccidentsTaskData,
  handleArnmAssigneeData,
  handleVehicleData,
  handleInsurancesData,
  getArnmAccidentTaskPayload,
} from './utils';

const stages = orderBy(
  Object.keys(ARNM_ACCIDENTS_TASK_STATUSES_NAMES).reduce((acc, cur) => {
    acc.push({
      text: ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].label,
      value: Number(cur),
      order: ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].order,
    });

    return acc;
  }, []),
  'order'
);
export const useArnmAccidentsTaskOperations = () => {
  const router = useRouter();
  const { assignees, isFetching: isAssigneesFetching } = useAssignees();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();
  const arnmAccidentsTaskId = router.query?.arnmAccidentsTaskId;

  const arnmAccidentsTaskResponse = useQuery(
    [QUERY_KEYS.GET_ARNM_ACCIDENTS_TASK_KEY, arnmAccidentsTaskId],
    () => boardsServices.getTaskById(arnmAccidentsTaskId),
    {
      enabled: !!arnmAccidentsTaskId,
    }
  );

  const carId = arnmAccidentsTaskResponse?.data?.data?.customFields?.carId;

  const vehicleResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_BY_INTERNAL_ID_KEY, carId],
    () => fleetVehiclesServices.getVehicleByInternalId(carId),
    {
      enabled: arnmAccidentsTaskResponse.isFetched && !!carId,
    }
  );

  const vehicle = handleVehicleData(vehicleResponse?.data?.data);
  const arnmAssignees = handleArnmAssigneeData(assignees);

  const servicePointsEnumsResponse = useQuery(
    QUERY_KEYS.GET_SERVICE_POINTS_ENUMS_KEY,
    () => servicePointsServices.getServicePointsEnums()
  );

  const insurances = handleInsurancesData(
    servicePointsEnumsResponse?.data?.data?.supportedInsurances
  );

  const arnmAccidentsTask = handleArnmAccidentsTaskData(
    arnmAccidentsTaskResponse?.data?.data,
    servicePoints,
    assignees,
    stages
  );

  const updateTask = async ({ values, images, documents }) => {
    try {
      const imagesToUpload = images?.filter((img) => img?.localFile);
      const imagesToPatch = images?.filter(
        (img) => img?.isPatched && !img?.localFile
      );
      const documentsToUpload = documents?.filter((doc) => doc?.localFile);
      const documentsToPatch = documents?.filter(
        (doc) => doc?.isPatched && !doc?.localFile
      );
      const payload = getArnmAccidentTaskPayload(values);
      await boardsServices.updateTask(arnmAccidentsTaskId, payload);

      await asyncForEach(imagesToUpload, async (img) => {
        await boardsServices.uploadTaskImage(
          arnmAccidentsTaskId,
          img?.file,
          img?.tags
        );
      });
      await asyncForEach(imagesToPatch, async (img) => {
        await boardsServices.updateTaskImage(arnmAccidentsTaskId, img?.id, {
          tags: img?.tags,
        });
      });

      await asyncForEach(documentsToUpload, async (doc) => {
        await boardsServices.uploadTaskDocument(
          arnmAccidentsTaskId,
          doc?.file,
          '',
          doc?.tags
        );
      });

      await asyncForEach(documentsToPatch, async (doc) => {
        await boardsServices.updateTaskDocument(arnmAccidentsTaskId, doc?.id, {
          tags: doc?.tags,
        });
      });
    } catch (error) {
      showError(error);
    }
  };

  const loading =
    arnmAccidentsTaskResponse?.isFetching ||
    vehicleResponse?.isFetching ||
    isServicePointsFetching ||
    isAssigneesFetching ||
    servicePointsEnumsResponse?.isFetching;

  useEffect(() => {
    if (arnmAccidentsTaskResponse?.isError) {
      showError(arnmAccidentsTaskResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arnmAccidentsTaskResponse?.isError]);

  useEffect(() => {
    if (vehicleResponse?.isError) {
      showError(vehicleResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleResponse?.isError]);

  useEffect(() => {
    if (servicePointsEnumsResponse?.isError) {
      showError(servicePointsEnumsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicePointsEnumsResponse?.isError]);

  return {
    loading,
    task: arnmAccidentsTask,
    taskId: arnmAccidentsTaskId,
    vehicle,
    servicePoints,
    assignees,
    arnmAssignees,
    insurances,
    stages,
    updateTask,
    getArnmAccidentsTask: arnmAccidentsTaskResponse.refetch,
  };
};
